import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  PropsWithChildren,
} from "react";
import { WalkthroughProgressBar } from "./WalkthroughProgressBar";
import {
  ArrowsContextRequest,
  ArrowsContextResponse,
  IsShouldStartRequest,
  IsShouldStartResponse,
} from "../entities/Walkthrough";

interface WalkthroughState {
  isActive: boolean;
  currentStep: number;
  isPaused: boolean;
  followupQuestion?: string;
  isLoading: boolean;
}

interface WalkthroughContextValue {
  state: WalkthroughState;
  startWalkthrough: () => void;
  stopWalkthrough: () => void;
  setCurrentStep: (step: number) => void;
  setIsPaused: (paused: boolean) => void;
  setFollowupQuestion: (question?: string) => void;
  setIsLoading: (loading: boolean) => void;
  ensureTopbarVisible: () => void;
  onClickStop?: () => void;
  onClickContinue?: () => void;
  onSendFollowupResponse?: (response: string) => void;
  registerHandlers: (
    onClickStop: () => void,
    onClickContinue: () => void,
    onSendFollowupResponse: (response: string) => void,
  ) => void;
  getArrows: (
    request: Omit<ArrowsContextRequest, "chatSessionId">,
  ) => Promise<ArrowsContextResponse | undefined>;
  isShouldStart: (
    request: Omit<IsShouldStartRequest, "chatSessionId">,
  ) => Promise<IsShouldStartResponse | undefined>;
}

const WalkthroughContext = createContext<WalkthroughContextValue | undefined>(undefined);

interface WalkthroughProviderProps {
  getArrows?: (
    request: Omit<ArrowsContextRequest, "chatSessionId">,
  ) => Promise<ArrowsContextResponse | undefined>;
  isShouldStart?: (
    request: Omit<IsShouldStartRequest, "chatSessionId">,
  ) => Promise<IsShouldStartResponse | undefined>;
}

export function WalkthroughProvider({
  children,
  getArrows,
  isShouldStart,
}: PropsWithChildren<WalkthroughProviderProps>) {
  const [state, setState] = useState<WalkthroughState>({
    isActive: false,
    currentStep: 0,
    isPaused: false,
    followupQuestion: undefined,
    isLoading: false,
  });

  const [walkthroughKey, setWalkthroughKey] = useState(0);

  const handlersRef = useRef<{
    onClickStop?: () => void;
    onClickContinue?: () => void;
    onSendFollowupResponse?: (response: string) => void;
  }>({});

  const startWalkthrough = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: true,
      currentStep: 0,
      isPaused: false,
    }));
    setWalkthroughKey((prev) => prev + 1); // Increment key to force remount
  }, []);

  const stopWalkthrough = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: false,
      currentStep: 0,
      isPaused: false,
      followupQuestion: undefined,
    }));
  }, []);

  const setCurrentStep = useCallback((step: number) => {
    setState((prev) => ({
      ...prev,
      currentStep: step,
    }));
  }, []);

  const setIsPaused = useCallback((paused: boolean) => {
    const stack = new Error().stack;
    console.log("setIsPaused called with:", paused, "Stack trace:", stack);
    setState((prev) => {
      console.log("setIsPaused - prev state:", prev.isPaused, "-> new state:", paused);
      return {
        ...prev,
        isPaused: paused,
      };
    });
  }, []);

  const setFollowupQuestion = useCallback((question?: string) => {
    setState((prev) => ({
      ...prev,
      followupQuestion: question,
    }));
  }, []);

  const setIsLoading = useCallback((loading: boolean) => {
    setState((prev) => ({
      ...prev,
      isLoading: loading,
    }));
  }, []);

  const ensureTopbarVisible = useCallback(() => {
    setWalkthroughKey((prev) => prev + 1); // Force remount to reset isVisible to true
  }, []);

  const registerHandlers = useCallback(
    (
      onClickStop: () => void,
      onClickContinue: () => void,
      onSendFollowupResponse: (response: string) => void,
    ) => {
      handlersRef.current = {
        onClickStop,
        onClickContinue,
        onSendFollowupResponse,
      };
    },
    [],
  );

  const handleGetArrows = useCallback(
    (request: Omit<ArrowsContextRequest, "chatSessionId">) => {
      return getArrows?.(request) ?? Promise.resolve(undefined);
    },
    [getArrows],
  );

  const handleIsShouldStart = useCallback(
    (request: Omit<IsShouldStartRequest, "chatSessionId">) => {
      return isShouldStart?.(request) ?? Promise.resolve(undefined);
    },
    [isShouldStart],
  );

  const contextValue: WalkthroughContextValue = React.useMemo(
    () => ({
      state,
      startWalkthrough,
      stopWalkthrough,
      setCurrentStep,
      setIsPaused,
      setFollowupQuestion,
      setIsLoading,
      ensureTopbarVisible,
      onClickStop: handlersRef.current.onClickStop,
      onClickContinue: handlersRef.current.onClickContinue,
      onSendFollowupResponse: handlersRef.current.onSendFollowupResponse,
      registerHandlers,
      getArrows: handleGetArrows,
      isShouldStart: handleIsShouldStart,
    }),
    [
      state,
      startWalkthrough,
      stopWalkthrough,
      setCurrentStep,
      setIsPaused,
      setFollowupQuestion,
      setIsLoading,
      ensureTopbarVisible,
      registerHandlers,
    ],
  );

  return (
    <WalkthroughContext.Provider value={contextValue}>
      {children}
      {state.isActive && (
        <WalkthroughProgressBar
          key={walkthroughKey}
          currentStep={state.currentStep}
          totalSteps={0}
          isPaused={state.isPaused}
          onClickStop={() => handlersRef.current.onClickStop?.()}
          onClickContinue={() => handlersRef.current.onClickContinue?.()}
          isLoading={state.isLoading}
          followupQuestion={state.followupQuestion}
          onSendFollowupResponse={(response) =>
            handlersRef.current.onSendFollowupResponse?.(response)
          }
        />
      )}
    </WalkthroughContext.Provider>
  );
}

export function useWalkthroughContext() {
  const context = useContext(WalkthroughContext);
  if (!context) {
    throw new Error("useWalkthroughContext must be used within WalkthroughProvider");
  }
  return context;
}
